import * as XLSX from 'xlsx';
import { LineItem, EquipmentItem, PVWireItem, PagePathsMap } from '../types';
import { calculateTotals } from './calculations';
import { parseSafeNumber, preventSpreadsheetFormula } from '../utils/helpers';

export interface ExportParams {
  pdfFileName?: string;
  lineItems: LineItem[];
  equipmentItems: EquipmentItem[];
  pvWireItems: PVWireItem[];
  pagePathsMap: PagePathsMap;
  drawnWireFeetText: string;
  drawnWirePositiveText: string;
  drawnWireNegativeText: string;
  drawnWireOtherText: string;
  hasOtherWire: boolean;
}

export function exportTakeoffToExcel(params: ExportParams): void {
  const {
    pdfFileName,
    lineItems,
    equipmentItems,
    pvWireItems,
    pagePathsMap,
    drawnWireFeetText,
    drawnWirePositiveText,
    drawnWireNegativeText,
    drawnWireOtherText,
    hasOtherWire,
  } = params;

  if (
    lineItems.length === 0 &&
    equipmentItems.length === 0 &&
    pvWireItems.length === 0 &&
    Object.keys(pagePathsMap).length === 0
  ) {
    alert('No data to export. Please process a file first.');
    return;
  }

  const wb = XLSX.utils.book_new();
  const formatKey = (key: string) => key.replace(/_/g, ' ');

  const { wireHashMap, conduitHashMap } = calculateTotals(lineItems, pvWireItems);

  // 1. Totals Sheet
  const totalsAOA: any[][] = [];

  totalsAOA.push(['--- WIRE TOTALS ---']);
  totalsAOA.push(['Item', 'Total Length (Ft)']);
  Object.keys(wireHashMap).forEach((key) => {
    totalsAOA.push([preventSpreadsheetFormula(key), wireHashMap[key]]);
  });

  totalsAOA.push([]);

  totalsAOA.push(['--- CONDUIT TOTALS ---']);
  totalsAOA.push(['Item', 'Total Length (Ft)']);
  Object.keys(conduitHashMap).forEach((key) => {
    totalsAOA.push([preventSpreadsheetFormula(key), conduitHashMap[key]]);
  });

  if (drawnWireFeetText && drawnWireFeetText !== '0 ft') {
    totalsAOA.push([]);
    totalsAOA.push(['--- ESTIMATED DRAWN PV WIRE ---']);
    totalsAOA.push(['Positive', drawnWirePositiveText]);
    totalsAOA.push(['Negative', drawnWireNegativeText]);
    if (hasOtherWire) {
      totalsAOA.push(['Other', drawnWireOtherText]);
    }
    totalsAOA.push(['Estimated Total Footprint', drawnWireFeetText]);
  }

  const wsTotals = XLSX.utils.aoa_to_sheet(totalsAOA);
  wsTotals['!cols'] = [{ wch: 25 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(wb, wsTotals, 'Totals');

  // 2. Raw Wire Schedule Sheet
  const mapKeysWithSpaces = (obj: Record<string, any>) => {
    return Object.keys(obj).reduce((acc: Record<string, any>, key: string) => {
      acc[preventSpreadsheetFormula(formatKey(key)) as string] = preventSpreadsheetFormula(obj[key]);
      return acc;
    }, {});
  };

  const cleanLineItems = lineItems.map((item) => {
    const copy = { ...item };
    if (copy.Max_Approx_Length) copy.Max_Approx_Length = parseSafeNumber(copy.Max_Approx_Length);
    if (copy.Qty_Wire) copy.Qty_Wire = parseSafeNumber(copy.Qty_Wire);
    if (copy.Qty_Parallel) copy.Qty_Parallel = parseSafeNumber(copy.Qty_Parallel);
    delete copy._bbox;
    return mapKeysWithSpaces(copy);
  });

  const wsMaterials = XLSX.utils.json_to_sheet(cleanLineItems);
  wsMaterials['!cols'] = Array(20).fill({ wch: 18 });

  if (pvWireItems.length > 0) {
    XLSX.utils.sheet_add_aoa(wsMaterials, [[]], { origin: -1 });
    XLSX.utils.sheet_add_aoa(wsMaterials, [['--- PV WIRE SCHEDULE ---']], { origin: -1 });

    const cleanPVWireItems = pvWireItems.map((item) => {
      const copy = { ...item };
      if (copy.max_distance_ft) copy.max_distance_ft = parseSafeNumber(copy.max_distance_ft);
      if (copy.Distance) copy.Distance = parseSafeNumber(copy.Distance);
      delete copy._bbox;
      return mapKeysWithSpaces(copy);
    });

    XLSX.utils.sheet_add_json(wsMaterials, cleanPVWireItems, { origin: -1, skipHeader: false });
  }

  XLSX.utils.book_append_sheet(wb, wsMaterials, 'Raw Wire Schedule');

  // 3. New Equipment Sheet
  const newEquipment = equipmentItems
    .filter((item) => item['Status']?.includes('N'))
    .map((item) => {
      const quantity = parseSafeNumber(item['Quantity']);
      return {
        Status: preventSpreadsheetFormula(item['Status']),
        Equipment: preventSpreadsheetFormula(item['Equipment']),
        Description: preventSpreadsheetFormula(item['Description']),
        Quantity: quantity,
        Rating: preventSpreadsheetFormula(item['Rating']),
        Type: preventSpreadsheetFormula(item['Type']),
        Enclosure: preventSpreadsheetFormula(item['Enclosure']),
        Voltage: preventSpreadsheetFormula(item['Voltage']),
      };
    });

  const wsEquipment = XLSX.utils.json_to_sheet(newEquipment);
  wsEquipment['!cols'] = [
    { wch: 10 },
    { wch: 30 },
    { wch: 45 },
    { wch: 10 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
  ];
  XLSX.utils.book_append_sheet(wb, wsEquipment, 'New Equipment');

  const baseFileName = pdfFileName ? pdfFileName.replace(/\.[^/.]+$/, '') : 'output';
  const excelFileName = `${baseFileName}_Takeoff.xlsx`;
  XLSX.writeFile(wb, excelFileName);
}
